import type { FormFieldV2 } from './form'
import type { SupportedLocale } from '@/i18n'
import type { TemplateLandingLocaleContent } from '@/features/templates/landing/configs/types'

export type TemplateCategory =
  | 'CREATORS'
  | 'MARKETING'
  | 'SALES'
  | 'PRODUCT'
  | 'HR'
  | 'OFFICE'
  | 'HEALTHCARE'
  | 'EDUCATION'
  | 'EVENTS'
  | 'SUPPORT'
  | 'PERSONAL'

export const TEMPLATE_CATEGORIES: TemplateCategory[] = [
  'CREATORS',
  'MARKETING',
  'SALES',
  'PRODUCT',
  'HR',
  'OFFICE',
  'HEALTHCARE',
  'EDUCATION',
  'EVENTS',
  'SUPPORT',
  'PERSONAL',
]

export const TEMPLATE_CATEGORY_META: Record<TemplateCategory, { label: string; icon: string }> = {
  CREATORS: { label: 'Creators', icon: 'Palette' },
  MARKETING: { label: 'Marketing', icon: 'Megaphone' },
  SALES: { label: 'Sales', icon: 'TrendingUp' },
  PRODUCT: { label: 'Product', icon: 'Package' },
  HR: { label: 'HR', icon: 'Users' },
  OFFICE: { label: 'Office', icon: 'Building2' },
  HEALTHCARE: { label: 'Healthcare', icon: 'HeartPulse' },
  EDUCATION: { label: 'Education', icon: 'GraduationCap' },
  EVENTS: { label: 'Events', icon: 'CalendarDays' },
  SUPPORT: { label: 'Support', icon: 'LifeBuoy' },
  PERSONAL: { label: 'Personal', icon: 'User' },
}

export type TemplateVisibility = 'public' | 'personal'

export type TemplateSortOption = 'popular' | 'newest' | 'name'

export interface FormTemplate {
  id: number
  name: string
  description?: string
  category: TemplateCategory
  slug: string
  fields: FormFieldV2[]
  tags: string[]
  locale: SupportedLocale
  availableLocales: SupportedLocale[]
  marketingContent?: TemplateLandingLocaleContent | null
  previewImageUrl?: string
  usageCount: number
  visibility: TemplateVisibility
  accountId?: number
  system: boolean
  /** Needs advanced fields — a Free account is refused at use time. */
  premium?: boolean
  /** Cheapest plan slug that unlocks it. null when anyone can use it. */
  requiredPlan?: string | null
  sourceFormId?: number
  createdAt: string
  updatedAt: string
}

export interface FormTemplateFromAPI {
  id: number
  name: string
  description?: string
  category: TemplateCategory
  slug: string
  fields: FormFieldV2[]
  tags?: string[]
  locale?: string
  available_locales?: string[]
  marketing_content?: TemplateLandingLocaleContent | null
  preview_image_url?: string
  usage_count: number
  visibility: TemplateVisibility
  account_id?: number
  system: boolean
  premium?: boolean
  /** Cheapest plan slug that unlocks it. null when anyone can use it. */
  required_plan?: string | null
  source_form_id?: number
  created_at: string
  updated_at: string
}

export interface CreateFormTemplateData {
  sourceFormId: number
  name: string
  description?: string
  category: TemplateCategory
  visibility: TemplateVisibility
}

export interface TemplateFilters {
  category?: TemplateCategory
  /**
   * Hide templates the current plan cannot use. Applied server side, before
   * pagination, so the page stays full.
   */
  excludePremium?: boolean
  tags?: string[]
  search?: string
  visibility?: TemplateVisibility
  sort?: TemplateSortOption
  page?: number
  perPage?: number
}

export interface UseTemplateResult {
  id: number
  hashId: string
  name: string
  description?: string
  status: string
  locale: SupportedLocale
  createdAt: string
}
